// ============================================================================
// Lifespan v1 — persistence: append-only action log + snapshots + replay.
//
// The action log is the migration asset (design/CONTEXT.md migration contract):
// `replay(log, paramSet) -> state` is exactly what a v2 PlayerDO does on first
// attach. Because the engine is deterministic and settlement is chunk-invariant
// (G7), replaying the log reproduces a live run BIT-IDENTICALLY — proven in
// packages/engine/test/persistence.test.ts.
//
// This module is pure and environment-free: no clock reads, no storage. The
// caller supplies wall-clock time (createdAtUnix) and owns IndexedDB / file IO.
// ============================================================================

import {
  applyAdjustment,
  applyCard,
  applyPlan,
  cardsForYear,
  initState,
  settleTo,
  simulateLife,
} from './engine.ts';
import { isReviewEntry } from './types.ts';
import type {
  CardPolicy,
  LifeResult,
  LogEntry,
  Params,
  SimOpts,
  State,
  Strategy,
  YearRecord,
} from './types.ts';

export const SAVE_FORMAT = 'lifespan-save';
export const SAVE_FORMAT_VERSION = 1;

/** Deck seed + acceptance policy for a run (cards are deterministic from these). */
export interface SaveHeader {
  cards: boolean;
  cardSeed: number;
  cardPolicy: CardPolicy;
}

/** A point-in-time state capture for fast load without full replay. */
export interface Snapshot {
  atAge: number;
  state: State;
}

/** The on-disk / export save. JSON round-trips all number fields exactly. */
export interface SaveFile {
  format: typeof SAVE_FORMAT;
  formatVersion: number;
  paramSetVersion: string;
  /** wall-clock metadata only — never read by the engine */
  createdAtUnix: number;
  header: SaveHeader;
  log: LogEntry[];
  snapshot?: Snapshot;
}

const defaultHeader = (): SaveHeader => ({
  cards: false,
  cardSeed: 1,
  cardPolicy: 'default',
});

/** A fresh, empty save bound to a ParamSet version. */
export function newSave(
  P: Params,
  createdAtUnix: number,
  header: Partial<SaveHeader> = {},
): SaveFile {
  return {
    format: SAVE_FORMAT,
    formatVersion: SAVE_FORMAT_VERSION,
    paramSetVersion: P.paramSetVersion,
    createdAtUnix,
    header: { ...defaultHeader(), ...header },
    log: [],
  };
}

/** Append one filed plan (an annual-review action) to the log. */
export function appendAction(save: SaveFile, entry: LogEntry): void {
  save.log.push(entry);
}

/**
 * Snapshot the state tagged with its in-game age (death age if dead, else the
 * current settled age). structuredClone is lossless here — state has no
 * functions or class instances.
 */
export function snapshotAtAge(state: State, P: Params): Snapshot {
  const age = state.deathAge != null ? state.deathAge : P.startAge + state.n * P.substep;
  return { atAge: age, state: structuredClone(state) };
}

/**
 * Replay a save's action log into a final state + per-year history.
 *
 * The log may contain two entry kinds (types.ts): annual **reviews** at
 * integral ages (full applyPlan) and mid-year **adjustments** at any
 * substep-aligned age (policy only). Each replayed year opens with its review,
 * then adjustments and card events interleave in time order within the year
 * (ties: log entries before cards), then the year settles to its boundary —
 * for an annual-only log this is the exact settleTo/applyPlan/applyCard
 * sequence `stepYear` produces, so the result is bit-identical to the live run
 * the log was recorded from (G7: settlement is chunk-pattern invariant).
 *
 * The replay extent is defined by review entries: each year consumes exactly
 * one review; the replay stops at the first year without one. Adjustments
 * beyond the last reviewed year are ignored (a well-formed log never has them).
 */
export function replay(save: SaveFile, P: Params): { state: State; yearly: YearRecord[] } {
  const st = initState(P);
  const yearly: YearRecord[] = [];
  const cardsOn = save.header.cards;
  const cardSeed = save.header.cardSeed;
  const cardPolicy: CardPolicy = save.header.cardPolicy;
  const entries = [...save.log].sort((a, b) => a.atAge - b.atAge); // stable: equal ages keep log order
  let ei = 0;
  let year = 0;

  for (;;) {
    if (!st.alive) break;
    // the year opens with its review (absent review = end of the log's extent)
    const review = entries[ei];
    if (
      !review ||
      !isReviewEntry(review) ||
      Math.abs(review.atAge - (P.startAge + year)) > 1e-9
    ) {
      break;
    }
    ei += 1;
    applyPlan(st, review.plan, P);

    const yearEnd = P.startAge + year + 1;
    const cardEvs = cardsOn ? cardsForYear(year, cardSeed, P) : [];
    let ci = 0;
    while (st.alive) {
      const adj = ei < entries.length && entries[ei]!.atAge < yearEnd - 1e-9 ? entries[ei]! : null;
      const adjT = adj ? adj.atAge : Infinity;
      const cardT = ci < cardEvs.length ? P.startAge + year + cardEvs[ci]!.t : Infinity;
      if (adjT === Infinity && cardT === Infinity) break;
      if (adjT <= cardT) {
        settleTo(st, adjT, P);
        if (!st.alive) break;
        // a same-age second review would have broken the outer loop contract;
        // validated logs only carry adjustments inside a year
        applyAdjustment(st, adj!.plan);
        ei += 1;
      } else {
        settleTo(st, cardT, P);
        if (!st.alive) break;
        applyCard(st, cardEvs[ci]!, cardPolicy, P);
        ci += 1;
      }
    }
    settleTo(st, yearEnd, P);
    year += 1;
    yearly.push({
      age: P.startAge + year,
      S: st.S,
      M: st.M,
      H: st.H,
      K: st.K,
      mode: review.plan.mode,
      livingFrac: review.plan.livingFrac,
    });
  }
  return { state: st, yearly };
}

/**
 * Convenience: run a strategy live while recording its log, and package the
 * result with a ready-to-persist save. The live LifeResult and the save's log
 * are guaranteed to replay to the same state (persistence.test.ts asserts it).
 */
export function recordLife(
  strategy: Strategy,
  P: Params,
  createdAtUnix: number,
  header: Partial<SaveHeader> = {},
): { result: LifeResult; save: SaveFile } {
  const save = newSave(P, createdAtUnix, header);
  const opts: SimOpts = {
    cards: save.header.cards,
    cardSeed: save.header.cardSeed,
    cardPolicy: save.header.cardPolicy,
    record: save.log,
  };
  const result = simulateLife(strategy, P, opts);
  return { result, save };
}

// ---------------------------------------------------------------------------
// The counterfactual re-sim (GDD §9.3) — the game's argument made personal.
// ---------------------------------------------------------------------------

export interface CounterfactualResult {
  estate: number;
  deathAge: number;
  K: number;
  skillAges: number[];
}

/**
 * Replay THIS life with every Living dollar banked instead (GDD §9.3): the
 * experience dial forced to $0 in every entry, sabbatical years run as
 * Standard, every card resolved to its default, historical enrolments
 * attempted and failing where gates fail (zero spend collapses M*, so study
 * stalls where it stalls — that is the point). If the transformed life
 * outlives the log's extent, the last transformed plan continues to its own
 * death ("last year's policy continues", §3.2). Deterministic: the same save
 * always yields the same counterfactual.
 */
export function counterfactual(save: SaveFile, P: Params): CounterfactualResult {
  const banked: SaveFile = {
    ...save,
    header: { ...save.header, cardPolicy: 'default' },
    log: save.log.map((e) => ({
      ...e,
      plan: {
        mode: e.plan.mode === 'sab' ? 'std' : e.plan.mode,
        livingFrac: 0,
        study: e.plan.study,
      },
    })),
  };
  const { state: st } = replay(banked, P);
  if (st.alive) {
    // continue the final plan (already transformed) until death or the guard
    const last = banked.log[banked.log.length - 1];
    const plan = last ? { ...last.plan } : { mode: 'std' as const, livingFrac: 0, study: false };
    // the replay's alive-exit always lands on a whole year boundary
    let year = Math.round(st.n * P.substep);
    while (st.alive && year < P.maxAge - P.startAge) {
      applyPlan(st, { ...plan }, P);
      settleTo(st, P.startAge + year + 1, P);
      year += 1;
    }
  }
  return {
    estate: st.S,
    deathAge: st.deathAge ?? P.startAge + st.n * P.substep,
    K: st.K,
    skillAges: st.skillAges,
  };
}

// ---------------------------------------------------------------------------
// The estate record (GDD §9.3) — byte-for-byte the v2 inheritance object.
// ---------------------------------------------------------------------------

export type Disposition = 'charity' | 'kin' | 'monument' | 'unanswered';

export interface EstateRecord {
  paramSetVersion: string;
  /** card deck seed, present only when the life was played with cards on */
  seed?: number;
  estate: number;
  age: number;
  skills: { K: number; skillAges: number[] };
  /** from The Legacy Question (§8E.2); 'unanswered' until M7 ships the card */
  disposition: Disposition;
  ledger: {
    earned: number;
    interest: number;
    costOfLiving: number;
    living: number;
    tuition: number;
    cards: number;
    /** share of a POSITIVE estate that was compound interest (G12's number);
     * 0 when the estate is non-positive — for a debt-death the ratio would be
     * "share of the debt that was overdraft interest", a different number
     * under the same name */
    interestShare: number;
    flourishYears: number;
    /** peak overdraft (minimum cash ever held) */
    minCash: number;
  };
}

/** Build the estate record from a dead (or final) state. */
export function estateRecordOf(
  st: State,
  save: SaveFile,
  P: Params,
  disposition: Disposition = 'unanswered',
): EstateRecord {
  const rec: EstateRecord = {
    paramSetVersion: P.paramSetVersion,
    estate: st.S,
    age: st.deathAge ?? P.startAge + st.n * P.substep,
    skills: { K: st.K, skillAges: [...st.skillAges] },
    disposition,
    ledger: {
      earned: st.earnedAccum,
      interest: st.interestAccum,
      costOfLiving: st.colAccum,
      living: st.livingAccum,
      tuition: st.tuitionAccum,
      cards: st.cardCashAccum,
      interestShare: st.S > 0 ? st.interestAccum / st.S : 0,
      flourishYears: st.flourishYears,
      minCash: st.minS,
    },
  };
  if (save.header.cards) rec.seed = save.header.cardSeed;
  return rec;
}

/** Serialize a save to a compact JSON string (numbers round-trip exactly). */
export function serialize(save: SaveFile): string {
  return JSON.stringify(save);
}

/** Parse and validate a save string. Throws on a malformed / wrong-format blob. */
export function deserialize(str: string): SaveFile {
  const obj = JSON.parse(str) as SaveFile;
  if (obj.format !== SAVE_FORMAT) {
    throw new Error(`not a Lifespan save (format=${String(obj.format)})`);
  }
  if (typeof obj.formatVersion !== 'number') {
    throw new Error('save missing formatVersion');
  }
  if (!Array.isArray(obj.log)) {
    throw new Error('save missing action log');
  }
  return obj;
}
